import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AlyceTheme, Icon, Button } from '@alycecom/ui';
import { useDispatch, useSelector } from 'react-redux';

import { IDisplayedTag, ISelectedTags, ITagData } from '../../store/tags/tags.types';
import { getAvailableTags, getIsAvailableTagsLoading, getIsSaveInProgress } from '../../store/tags/tags.selectors';
import { loadAvailableTagsRequest } from '../../store/tags/tags.actions';
import { shuffleArray } from '../../../../helpers/list.helpers';
import useTrackRecipient from '../../../../hooks/useTrackRecipient';

import TagsList from './TagsList';
import DelayedButton from './DelayedButton';

const useStyles = makeStyles<AlyceTheme>(({ palette, spacing, breakpoints, zIndex }) => ({
  header: {
    paddingBottom: spacing(3),
    [breakpoints.down('lg')]: {
      padding: spacing(1, 2, 2, 2),
    },
  },
  content: {
    display: 'flex',
    justifyContent: 'center',

    padding: spacing(1, 3.5),
    [breakpoints.down('lg')]: {
      padding: spacing(1, 2.5),
    },
  },
  icon: {
    width: '12px!important',
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: palette.primary.main,
    [breakpoints.down('lg')]: {
      fontSize: 24,
    },
  },
  actionsContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    padding: spacing(2, 4),
    boxShadow: '0px -25px 30px 20px #fff',
    zIndex: zIndex.modal,
  },
  disclaimer: {
    fontSize: 14,
    textAlign: 'center',
    width: '100%',
    marginLeft: 0,
    marginTop: spacing(1.2),
    color: palette.text.secondary,

    [breakpoints.up(750)]: {
      width: 570,
      fontSize: 16,
    },
  },
  modalBackdrop: {
    backdropFilter: 'blur(8px)',
  },
  loadingBackdrop: {
    position: 'absolute',
    zIndex: 'auto',
  },
}));

const MIN_TAGS = 3;

export interface ITagsSelectionModalProps {
  isOpen: boolean;
  onSave: (selectedTags: ISelectedTags) => void;
  onCancel: () => void;
  hashId: string;
}

const TagsSelectionModal = ({ isOpen, onSave, onCancel, hashId }: ITagsSelectionModalProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const trackEvent = useTrackRecipient();

  const tags = useSelector(getAvailableTags);
  const isLoading = useSelector(getIsAvailableTagsLoading);
  const isSaveInProgress = useSelector(getIsSaveInProgress);

  const shuffledTags = useMemo<(IDisplayedTag & ITagData)[]>(
    () =>
      shuffleArray([...tags]).map((tag: ITagData, index: number) => ({
        ...tag,
        position: index,
      })),
    [tags],
  );

  const [selected, setSelected] = useState<number[]>([]);
  const handleChangeSelected = useCallback((newSelected: number[]) => {
    setSelected(newSelected);
  }, []);

  const handleSubmit = useCallback(() => {
    onSave({ tagIds: selected, displayedTags: shuffledTags });
    trackEvent('Declared data - Submit', { tagIds: selected });
  }, [onSave, selected, shuffledTags, trackEvent]);

  const handleSkip = useCallback(() => {
    onSave({ tagIds: [], displayedTags: shuffledTags });
    trackEvent('Declared data - Skip');
  }, [onSave, shuffledTags, trackEvent]);

  const handleBack = useCallback(() => {
    onCancel();
    trackEvent('Declared data - Back');
  }, [onCancel, trackEvent]);

  useEffect(() => {
    if (isOpen) {
      dispatch(loadAvailableTagsRequest({ hashId }));
      setSelected([]);
    }
  }, [dispatch, hashId, isOpen]);

  return (
    <Dialog
      open={isOpen}
      maxWidth={false}
      data-testid="DeclaredData.Modal"
      BackdropProps={{ classes: { root: classes.modalBackdrop } }}
    >
      <DialogTitle className={classes.header}>
        <Box display="flex" justifyContent="space-between">
          <Button
            variant="text"
            onClick={handleBack}
            startIcon={<Icon icon="arrow-left" className={classes.icon} />}
            data-testid="DeclaredData.Back"
          >
            Back
          </Button>
          <DelayedButton
            variant="text"
            delay={5000}
            onClick={handleSkip}
            endIcon={<Icon icon="arrow-right" className={classes.icon} />}
            data-testid="DeclaredData.Skip"
          >
            Skip
          </DelayedButton>
        </Box>
        <Box textAlign="center">
          <Typography className={classes.title}>Find something you will love</Typography>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <TagsList
          tags={shuffledTags}
          selected={selected}
          onChangeSelected={handleChangeSelected}
          isLoading={isLoading}
        />
      </DialogContent>
      <DialogActions classes={{ root: classes.actionsContainer }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={selected.length < MIN_TAGS}
          onClick={handleSubmit}
          data-testid="DeclaredData.Save"
        >
          {selected.length >= MIN_TAGS ? 'Show me the magic!' : `Choose ${MIN_TAGS - selected.length} more`}
        </Button>
        <Typography className={classes.disclaimer}>
          Choose three or more categories so we can suggest the best gift for you now and when others send you gifts in
          the future.
        </Typography>
      </DialogActions>

      <Backdrop className={classes.loadingBackdrop} open={isSaveInProgress} timeout={0}>
        <CircularProgress variant="indeterminate" color="secondary" size={60} />
      </Backdrop>
    </Dialog>
  );
};

export default memo(TagsSelectionModal);
